import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { CheckCircle2, ArrowLeft, ChevronDown } from 'lucide-react';
import { usePageMeta } from '../lib/usePageMeta';

const faqs = [
  { q: "What is Wricoo?", a: "Wricoo is a collaborative platform built for content teams responsible for managing, creating, and preparing website content such as blogs and articles. It brings SEO briefs, writing, editing, and collaboration into one structured workspace." },
  { q: "Is Wricoo a CMS?", a: "No. Wricoo is not a publishing CMS like WordPress. While it includes structured content management features, it does not allow direct website publishing. It is a private workspace designed to improve content workflow efficiency." },
  { q: "Does Wricoo generate AI content?", a: "No. Wricoo is not an AI content generator. It is built for structured collaboration and accountability, not automated writing." },
  { q: "Can I publish content directly from Wricoo?", a: "No. Content created inside Wricoo must be exported and published separately using your preferred CMS or website platform." },
  { q: "Is Wricoo suitable for solo creators?", a: "Wricoo is built specifically for teams. It is designed for structured collaboration between SEO specialists, writers, editors, and content managers." },
  { q: "What is a Workspace?", a: "A Workspace is a collaborative environment where you can invite team members, assign roles, and manage content projects together. Each workspace operates independently, making it ideal for separating clients or departments." },
  { q: "Can I create multiple Workspaces?", a: "Yes. During the 30-day free trial, you can create multiple workspaces. You can think of it as one workspace per client or department." },
  { q: "How many members can I invite?", a: "Free Plan: Up to 5 members. In-House Teams Plan: Up to 10 members. Agency Plan: Up to 20 members. Additional members can be added through paid Add-Ons." },
  { q: "Can SEO and writers collaborate in the same document?", a: "Yes. SEO specialists, writers, and editors can work within the same workspace, reducing document duplication and back-and-forth confusion." },
  { q: "Do you offer a free plan?", a: "Yes. Wricoo offers a free forever plan with limited features so teams can explore the platform before upgrading." },
  { q: "Can I cancel my subscription?", a: "Yes. You can cancel your subscription at any time. However, all payments are non-refundable. We recommend using the 30-day trial before selecting a paid plan." },
  { q: "Can agencies track actual word output?", a: "Yes. Agencies can track the number of words written by individual writers compared to the total document word count, enabling more accurate billing and accountability." },
  { q: "Can users upload images or media files?", a: "No. Wricoo currently supports text-based collaboration only. Media uploads are not available at this time." },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "What is Wricoo?", "acceptedAnswer": { "@type": "Answer", "text": "Wricoo is a collaborative platform built for content teams responsible for managing, creating, and preparing website content such as blogs and articles. It brings SEO briefs, writing, editing, and collaboration into one structured workspace." } },
    { "@type": "Question", "name": "Is Wricoo a CMS?", "acceptedAnswer": { "@type": "Answer", "text": "No. Wricoo is not a publishing CMS like WordPress. It does not allow direct website publishing and is designed to improve content workflow efficiency." } },
    { "@type": "Question", "name": "Does Wricoo generate AI content?", "acceptedAnswer": { "@type": "Answer", "text": "No. Wricoo is not an AI content generator. It is built for structured collaboration and accountability." } },
    { "@type": "Question", "name": "Can I publish content directly from Wricoo?", "acceptedAnswer": { "@type": "Answer", "text": "No. Content must be exported and published separately using your own CMS." } },
    { "@type": "Question", "name": "Is Wricoo suitable for solo creators?", "acceptedAnswer": { "@type": "Answer", "text": "Wricoo is designed specifically for teams such as SEO specialists, writers, editors, and content managers." } },
    { "@type": "Question", "name": "What is a Workspace?", "acceptedAnswer": { "@type": "Answer", "text": "A Workspace is a collaborative environment where you can invite team members, assign roles, and manage content projects together." } },
    { "@type": "Question", "name": "How many members can I invite?", "acceptedAnswer": { "@type": "Answer", "text": "Free Plan allows up to 5 members. In-House Teams Plan allows up to 10 members. Agency Plan allows up to 20 members. Additional members can be added via Add-Ons." } },
    { "@type": "Question", "name": "Can agencies track actual word output?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Agencies can track individual writer word counts compared to total document word count." } },
    { "@type": "Question", "name": "Can users upload images or media files?", "acceptedAnswer": { "@type": "Answer", "text": "No. Wricoo currently supports text-based collaboration only." } },
  ]
};

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200" data-testid="faq-item">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left gap-4"
        data-testid="faq-toggle"
      >
        <span className="text-sm font-medium text-gray-900">{question}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <p className="pb-4 text-sm text-gray-600 leading-relaxed">{answer}</p>
      )}
    </div>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');

  usePageMeta({
    title: 'Pricing - Wricoo',
    description: 'Simple, transparent pricing for content teams. Start free, upgrade when you need more.',
    robots: 'index, follow',
    canonical: 'https://wricoo.com/pricing/',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema';
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('faq-schema');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">

      {/* Header */}
      <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-blue-200 hover:text-white transition">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <Link to="/" className="text-xl font-bold text-white tracking-tight" data-testid="pricing-logo">Wricoo</Link>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-blue-500 hover:text-white" onClick={() => navigate('/login/')}>Sign In</Button>
            <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => navigate('/register/')}>Get Started</Button>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="py-20 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto">
              Start free. Upgrade when you need more power.
            </p>
          </div>

          {/* Monthly / Yearly Toggle */}
          <div className="flex items-center justify-center gap-3 mb-10" data-testid="pricing-toggle">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>Monthly</span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative w-12 h-6 rounded-full transition-colors ${billingCycle === 'yearly' ? 'bg-blue-600' : 'bg-gray-300'}`}
              data-testid="billing-cycle-toggle"
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${billingCycle === 'yearly' ? 'translate-x-6' : ''}`} />
            </button>
            <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-400'}`}>Yearly</span>
            {billingCycle === 'yearly' && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Save 15%</span>}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Free Plan */}
            <div className="rounded-xl border border-gray-200 p-6 bg-white flex flex-col" data-testid="plan-free">
              <h3 className="text-base font-bold text-gray-900 mb-1">Forever Free</h3>
              <p className="text-3xl font-bold text-gray-900 mb-4">$0<span className="text-sm font-normal text-gray-500">/month</span></p>
              <ul className="space-y-2.5 mb-6 flex-1">
                {['1 Workspace', '5 Members', '30 Articles', 'Preview Mode'].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Button variant="outline" className="w-full" onClick={() => navigate('/register/')}>Get Started</Button>
            </div>

            {/* Trial */}
            <div className="rounded-xl border border-blue-200 p-6 bg-blue-50/50 flex flex-col" data-testid="plan-trial">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-bold text-gray-900">30-Day Trial</h3>
                <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">NO CARD</span>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-4">$0<span className="text-sm font-normal text-gray-500"> for 30 days</span></p>
              <ul className="space-y-2.5 mb-6 flex-1">
                {['All Agency Features', 'No Credit Card Required', '10 Members', 'Unlimited Articles', 'HTML Export'].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Button className="w-full" onClick={() => navigate('/register/')}>Start Trial</Button>
            </div>

            {/* In-House */}
            <div className="rounded-xl border-2 border-blue-500 p-6 bg-white flex flex-col relative" data-testid="plan-inhouse">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[10px] px-3 py-1 rounded-full font-medium">POPULAR</span>
              <h3 className="text-base font-bold text-gray-900 mb-1">In-House Teams</h3>
              <p className="text-3xl font-bold text-gray-900 mb-1">
                ${billingCycle === 'yearly' ? '16.15' : '19'}
                <span className="text-sm font-normal text-gray-500">/month</span>
              </p>
              {billingCycle === 'yearly' && <p className="text-xs text-green-600 font-medium mb-4">Billed $193.80/year</p>}
              {billingCycle === 'monthly' && <p className="text-xs text-green-600 font-medium mb-4">15% off with annual billing</p>}
              <ul className="space-y-2.5 mb-6 flex-1">
                {['1 Workspace', '6 Members', 'Unlimited Articles', 'Preview Mode', 'HTML Export'].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Button className="w-full" onClick={() => navigate('/register/')}>Get Started</Button>
            </div>

            {/* Agency */}
            <div className="rounded-xl border border-gray-200 p-6 bg-white flex flex-col" data-testid="plan-agency">
              <h3 className="text-base font-bold text-gray-900 mb-1">Agency</h3>
              <p className="text-3xl font-bold text-gray-900 mb-1">
                ${billingCycle === 'yearly' ? '24.65' : '29'}
                <span className="text-sm font-normal text-gray-500">/month</span>
              </p>
              {billingCycle === 'yearly' && <p className="text-xs text-green-600 font-medium mb-4">Billed $295.80/year</p>}
              {billingCycle === 'monthly' && <p className="text-xs text-green-600 font-medium mb-4">15% off with annual billing</p>}
              <ul className="space-y-2.5 mb-6 flex-1">
                {['2 Workspaces', '10 Members', 'Unlimited Articles', 'Preview Mode', 'HTML Export'].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Button className="w-full" onClick={() => navigate('/register/')}>Get Started</Button>
            </div>
          </div>

          {/* Add-ons */}
          <div className="mt-10 text-center">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Need More? Add-Ons Available</h3>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
              <span className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">+1 Workspace — $5/mo</span>
              <span className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">+1 Member — $7/mo</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="font-bold text-gray-900 mb-2">Frequently Asked Questions</h2>
            <p className="text-sm text-gray-600">Everything you need to know about Wricoo.</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 px-6">
            {faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Wricoo. All rights reserved.</p>
          <nav className="flex gap-4 text-sm text-gray-500">
            <a href="/about/" className="hover:text-gray-900 transition">About</a>
            <a href="/pricing/" className="hover:text-gray-900 transition">Pricing</a>
            <a href="/tools/" className="hover:text-gray-900 transition">Tools</a>
            <a href="/privacy-policy/" className="hover:text-gray-900 transition">Privacy</a>
            <a href="/terms-and-conditions/" className="hover:text-gray-900 transition">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
